import * as React from 'react';
import { useTheme } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { InfiniteData } from '@tanstack/react-query';
import { Folder } from '../../../../../types';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { StyledTreeItem } from './styled-drive-tree-item';
import { UseFormSetValue } from 'react-hook-form';
import { DriveSweepFormValues } from '../../../drive/drive-sweep/create-drive-sweep/create-drive-sweep-form-page';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import AddToDrive from '@mui/icons-material/AddToDrive';
import FolderIcon from '@mui/icons-material/Folder';
import FolderShared from '@mui/icons-material/FolderShared';
import PeopleOutlineOutlined from '@mui/icons-material/PeopleOutlineOutlined';
import Star from '@mui/icons-material/Star';
interface DriveFolderTreeProps {
  driveId: string;
  userEmail: string;
  selectedFolder: string;
  setSelectedFolder: React.Dispatch<React.SetStateAction<string>>;
  allFolders: InfiniteData<Folder[]> | undefined;
  isLoading: boolean;
  displayJustDrive?: boolean;
  disabledNodes?: string[];
  setValue?: UseFormSetValue<DriveSweepFormValues>;
}

export default function DriveFolderTree({
  driveId,
  userEmail,
  setSelectedFolder,
  selectedFolder,
  allFolders,
  isLoading,
  displayJustDrive,
  disabledNodes,
  setValue,
}: DriveFolderTreeProps) {
  const theme = useTheme();

  const folders = allFolders?.pages.reduce((acc, page) => acc.concat(page), []);

  const uniqueFolders = folders?.filter(
    (value, index, self) => self.findIndex((v) => v.id === value.id) === index
  );

  // Function to convert the flat array into a tree structure
  function buildTree(folders: Folder[], parentId: string) {
    const tree: Folder[] = [];

    folders.forEach((folder) => {
      if (folder.parentId === parentId) {
        const children = buildTree(folders, folder.id).sort((a, b) =>
          a.title.localeCompare(b.title)
        );

        if (children.length > 0) {
          folder.children = children;
        }
        tree.push(folder);
      }
    });

    return tree;
  }

  const rootElements = buildTree(uniqueFolders || [], driveId).sort((a, b) =>
    a.title.localeCompare(b.title)
  );

  const renderTree = (folders: Folder[]) => {
    return folders.map((folder) => {
      const isShortcut =
        folder.mimeType === 'application/vnd.google-apps.shortcut';

      const targetTrashed =
        isShortcut && folder.shortcutDetails?.targetTrashed === true;
      const targetInaccessible =
        isShortcut && folder.shortcutDetails?.targetAccessible === false;

      const isShortcutVisuallyOrClickDisabled =
        targetTrashed || targetInaccessible;

      const isDisabled =
        disabledNodes?.includes(folder.id) || isShortcutVisuallyOrClickDisabled;

      let tooltipTitle = folder.title;
      if (targetTrashed) {
        tooltipTitle = `${folder.title} no longer exists.`;
      } else if (targetInaccessible) {
        tooltipTitle = `You need permission to access ${folder.title}.`;
      }

      return (
        <Tooltip
          title={tooltipTitle}
          key={folder.id}
          disableHoverListener={!isShortcutVisuallyOrClickDisabled}
        >
          <div>
            <StyledTreeItem
              itemId={folder.id}
              labelText={folder.title}
              labelIcon={
                folder.iconUrl.includes('shared') ? FolderShared : FolderIcon
              }
              labelInfo=""
              color={theme.palette.text.primary}
              bgColor={theme.palette.primary.light}
              isShortcut={isShortcut}
              disabled={isDisabled}
            >
              {Array.isArray(folder.children)
                ? renderTree(folder.children)
                : null}
            </StyledTreeItem>
          </div>
        </Tooltip>
      );
    });
  };

  if (isLoading) {
    return (
      <Stack gap={1} sx={{ py: 0.5 }}>
        <Skeleton variant="rounded" width={'100%'} height={30} />
        <Skeleton variant="rounded" width={'100%'} height={30} />
        <Skeleton variant="rounded" width={'100%'} height={30} />
        <Skeleton variant="rounded" width={'100%'} height={30} />
      </Stack>
    );
  } else {
    return (
      <Stack sx={{ flex: 1, py: 0.5 }}>
        <SimpleTreeView
          aria-label="drive folders"
          slots={{
            expandIcon: ArrowRightIcon,
            collapseIcon: ArrowDropDownIcon,
          }}
          defaultExpandedItems={[driveId]}
          sx={{
            p: 0.25,
          }}
          onSelectedItemsChange={(_event, nodeId) => {
            if (nodeId) {
              const selected = uniqueFolders?.find(
                (folder) => folder.id === nodeId
              );

              if (
                selected?.shortcutDetails?.targetMimeType.includes('folder') &&
                selected?.shortcutDetails?.targetId
              ) {
                setSelectedFolder(selected.shortcutDetails.targetId);
              } else {
                setSelectedFolder(nodeId);
              }

              if (setValue) {
                setValue('folderName', selected?.title || '');
              }
            }
          }}
          selectedItems={selectedFolder}
          multiSelect={false}
        >
          <StyledTreeItem
            itemId={driveId}
            labelText="My Drive"
            labelIcon={AddToDrive}
            color={theme.palette.text.primary}
            bgColor={theme.palette.primary.light}
            disabled={userEmail === ''}
          >
            {renderTree(rootElements)}
          </StyledTreeItem>
          {displayJustDrive ? null : (
            <>
              <StyledTreeItem
                itemId={`${driveId}.shared`}
                labelText="Shared with me"
                labelIcon={PeopleOutlineOutlined}
                color={theme.palette.text.primary}
                bgColor={theme.palette.primary.light}
                disabled={userEmail === ''}
              />
              <StyledTreeItem
                itemId={`${driveId}.starred`}
                labelText="Starred"
                labelIcon={Star}
                color={theme.palette.text.primary}
                bgColor={theme.palette.primary.light}
                disabled={userEmail === ''}
              />
              <StyledTreeItem
                itemId={`${driveId}.trash`}
                labelText="Trash"
                labelIcon={DeleteIcon}
                color={theme.palette.text.primary}
                bgColor={theme.palette.primary.light}
                disabled={userEmail === ''}
              />
            </>
          )}
        </SimpleTreeView>
      </Stack>
    );
  }
}
